import { useState } from 'react';
import { Check, Loader2, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, inputCls, requestStatusLabel, statusBadge } from '../../utils/operations';

export const PartnerRequests = ({ partner, requests, onRefresh }) => {
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState('');
  const assigned = requests.filter((r) => r.partner_name === partner.name);
  const actionable = assigned.filter((r) => ['request_sent', 'waiting_partner', 'pending'].includes(r.status));
  const EMPTY_FORM = { note: '', alternative_dates: '', supplier_price: '', supplier_currency: 'EUR', available_time: '', capacity: '', meeting_point: '', operational_notes: '', internal_notes: '' };
  const setForm = (id, key, value) => setForms((f) => ({ ...f, [id]: { ...EMPTY_FORM, ...f[id], [key]: value } }));

  const respond = async (request, action) => {
    const form = { ...EMPTY_FORM, ...forms[request.id] };
    setBusy(`${request.id}-${action}`);
    try {
      await apiFetch(`/partner/requests/${request.id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partner_name: partner.name,
          action,
          note: form.note,
          alternative_dates: form.alternative_dates,
          supplier_price: form.supplier_price === '' ? null : Number(form.supplier_price),
          supplier_currency: form.supplier_currency,
          available_time: form.available_time,
          capacity: form.capacity,
          meeting_point: form.meeting_point,
          operational_notes: form.operational_notes,
          internal_notes: form.internal_notes,
        }),
      });
      toast.success('Resposta enviada à Eco Collection');
      await onRefresh();
    } catch (err) {
      toast.error('Resposta não enviada', { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4" data-testid="partner-requests">
      {actionable.map((r) => {
        const form = { ...EMPTY_FORM, ...forms[r.id] };
        return (
          <article key={r.id} data-testid={`partner-request-${r.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-eco-ink">{r.reference} — {r.name}</p>
                <p className="mt-1 text-xs text-eco-ink/55">{r.destination}{r.product && ` · ${r.product}`}{r.experience && ` · ${r.experience}`}{r.package_name && ` · ${r.package_name}`}{(r.travel_date || r.dates) && ` · ${r.travel_date || r.dates}`}{r.travelers && ` · ${r.travelers}`}</p>
                {(r.alternative_date || r.children_ages || r.special_requirements || r.pickup_location) && (
                  <p className="mt-1 text-xs text-eco-ink/55" data-testid={`partner-request-details-${r.id}`}>
                    {[
                      r.alternative_date && `Data alternativa: ${r.alternative_date}`,
                      r.children_ages && `Idades crianças: ${r.children_ages}`,
                      r.pickup_location && `Recolha: ${r.pickup_location}`,
                      r.special_requirements && `Requisitos: ${r.special_requirements}`,
                    ].filter(Boolean).join(' · ')}
                  </p>
                )}
                {r.response_deadline && (
                  <p className="mt-1 text-xs font-semibold text-eco-gold" data-testid={`partner-request-deadline-${r.id}`}>
                    Responder até {new Date(r.response_deadline).toLocaleString('pt-PT')}
                  </p>
                )}
                {r.message && <p className="mt-2 text-sm italic text-eco-ink/55">“{r.message}”</p>}
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(r.status)}`}>{requestStatusLabel(r.status)}</span>
            </div>
            <div className="mt-4 grid gap-3 border-t border-eco-ink/10 pt-4 md:grid-cols-3">
              <input data-testid={`partner-supplier-price-${r.id}`} type="number" min="0" step="0.01" value={form.supplier_price} onChange={(e) => setForm(r.id, 'supplier_price', e.target.value)} placeholder="Preço fornecedor" className={inputCls} />
              <select data-testid={`partner-supplier-currency-${r.id}`} value={form.supplier_currency} onChange={(e) => setForm(r.id, 'supplier_currency', e.target.value)} className={inputCls}>
                {['EUR', 'USD', 'GBP', 'STN', 'CVE'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid={`partner-available-time-${r.id}`} value={form.available_time} onChange={(e) => setForm(r.id, 'available_time', e.target.value)} placeholder="Hora disponível (ex: 08:30)" className={inputCls} />
              <input data-testid={`partner-capacity-${r.id}`} value={form.capacity} onChange={(e) => setForm(r.id, 'capacity', e.target.value)} placeholder="Capacidade" className={inputCls} />
              <input data-testid={`partner-meeting-point-${r.id}`} value={form.meeting_point} onChange={(e) => setForm(r.id, 'meeting_point', e.target.value)} placeholder="Ponto de encontro" className={inputCls} />
              <input data-testid={`partner-alt-dates-${r.id}`} value={form.alternative_dates} onChange={(e) => setForm(r.id, 'alternative_dates', e.target.value)} placeholder="Data alternativa (se aplicável)" className={inputCls} />
              <input data-testid={`partner-note-${r.id}`} value={form.note} onChange={(e) => setForm(r.id, 'note', e.target.value)} placeholder="Nota operacional para o Admin" className={inputCls} />
              <input data-testid={`partner-operational-notes-${r.id}`} value={form.operational_notes} onChange={(e) => setForm(r.id, 'operational_notes', e.target.value)} placeholder="Notas operacionais" className={inputCls} />
              <input data-testid={`partner-internal-notes-${r.id}`} value={form.internal_notes} onChange={(e) => setForm(r.id, 'internal_notes', e.target.value)} placeholder="Notas internas (não visíveis ao cliente)" className={inputCls} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid={`partner-confirm-${r.id}`} onClick={() => respond(r, 'confirm')} className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-eco-green px-4 text-xs font-semibold text-white transition-colors hover:bg-eco-deep"><Check size={13} /> Available</button>
              <button data-testid={`partner-changes-${r.id}`} onClick={() => respond(r, 'confirm_changes')} className="inline-flex min-h-10 items-center rounded-lg border border-eco-green/40 px-4 text-xs font-semibold text-eco-green transition-colors hover:bg-eco-green/10">Available with changes</button>
              <button data-testid={`partner-alternative-${r.id}`} onClick={() => respond(r, 'alternative')} className="inline-flex min-h-10 items-center rounded-lg border border-eco-gold/50 px-4 text-xs font-semibold text-eco-gold transition-colors hover:bg-eco-gold/10">Suggest alternative</button>
              <button data-testid={`partner-decline-${r.id}`} onClick={() => respond(r, 'decline')} className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-red-200 px-4 text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"><X size={13} /> Unavailable</button>
              {busy.startsWith(r.id) && <Loader2 size={15} className="animate-spin text-eco-gold" />}
            </div>
          </article>
        );
      })}
      {actionable.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem pedidos pendentes para {partner.name}.</p>}
    </div>
  );
};

export const PartnerBookings = ({ partner, requests, vouchers }) => {
  const bookings = requests.filter((r) => r.partner_name === partner.name && ['payment_pending', 'confirmed', 'voucher_issued', 'completed'].includes(r.status));
  return (
    <div className="space-y-4" data-testid="partner-bookings">
      {bookings.map((r) => {
        const voucher = vouchers.find((v) => v.request_id === r.id);
        return (
          <article key={r.id} data-testid={`partner-booking-${r.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-eco-ink">{r.reference} · {r.name}</p><p className="mt-1 text-xs text-eco-ink/55">{r.destination}{r.experience && ` · ${r.experience}`}{r.dates && ` · ${r.dates}`}</p></div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(r.status)}`}>{requestStatusLabel(r.status)}</span>
              {voucher && <a href={`/vouchers/${voucher.code}`} target="_blank" rel="noopener noreferrer" data-testid={`partner-booking-voucher-${r.id}`} className="text-xs font-bold text-eco-gold">{voucher.code}</a>}
            </div>
          </article>
        );
      })}
      {bookings.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem bookings operacionais.</p>}
    </div>
  );
};

export const PartnerMessages = () => (
  <div className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-16 text-center" data-testid="partner-messages-unconfigured">
    <MessageSquare className="mx-auto text-eco-gold" size={24} />
    <p className="mt-3 font-serif text-lg font-semibold text-eco-ink">Mensagens automáticas</p>
    <p className="mx-auto mt-2 max-w-md text-sm text-eco-ink/50">WhatsApp Business automático fica disponível depois de configurar o serviço oficial. Nenhuma mensagem é simulada.</p>
  </div>
);
